import { InstagramDataAccount, InstagramProfile } from "src/types/analysis";
import { PromptMessage, PromptMessageUser } from "src/types/prompts";

export const getGeneralOverviewMessages = function (account: InstagramProfile) {
  var messages = [] as PromptMessage[];

  messages.push({
    user: PromptMessageUser.System,
    content: `#CONTEXT:
You are a professional marketer that is specialized in Instagram marketing.

#GOAL:
You need to review the Instagram Account and make an overview of the account.


#RESPONSE GUIDELINES:
- Provide a brief overview of the account, including the focus, content type, and potential audience.
- If there is no title, then say that this information is missing and recommend providing it 
- If there is no bio, then say that this information is missing and recommend providing it
- if an account is private, then say that the account is private and say that you can't see the content, otherwise use this information
- Evaluate the followers-to-following ratio. What does this ratio suggest about the account’s reach and influence?
- If the account is verified, mention it in the response

#RESPONSE EXAMPLE:
The Instagram account **"istashko"** is managed by Ira Сташко, whose focus revolves around blogging, video creation, storytelling, and a unique segment called "КІНО НА ХВИЛИНУ".  The bio suggests that the content is centered on tips and insights related to content creation, the art of blogging, as well as the personal life. 

Ira is the founder of «Школу контенту» indicating her role in educating others about content creation. Additionally, she runs a content-focused club named «Йосип драний». Ira also emphasizes her passion for filming around the world, which suggests that her content might include travel-related videos. Her bio invites visitors to learn from her, implying she offers educational resources or courses linked in her profile. 

Overall, the account focused on individuals interested in storytelling, content creation, and potentially those looking for inspiration in digital media and filmmaking.

`,
  });

  messages.push({
    user: PromptMessageUser.User,
    content: `Username:
${account.username}

Title:
${account.data.fullName}

Bio:
${account.data.biography}

External link:
${account.data.externalUrl}

Followers:
${account.data.followersCount}

Following:
${account.data.followsCount}

${account.data.private ? "Private:\nYes" : ""}

${account.data.verified ? "Verified:\nYes" : ""}

${account.data.joinedRecently ? "Joined recently:\nYes" : ""}

${account.data.isBusinessAccount ? "Is business account:\nYes" : ""}
`,
  });
  return messages;
};

export const getGeneralContentReviewMessages = function (
  account: InstagramProfile
) {
  var messages = [] as PromptMessage[];

  messages.push({
    user: PromptMessageUser.System,
    content: `#CONTEXT:
You are a professional marketer that is specialized in Instagram marketing.
  
Here is a description of the Instagram Account.
  
#DESCRIPTION:
${account.generatedData.overview}
  
#LAST POST DATE:
${account.data.lastPostDate}
  
#POSTS IN THE LAST 30 DAYS:
${account.data.postsInLast30Days}
  
  
#GOAL:
You need to review the posts from the Instagram Account. Provide an overview of the content.
1. Tone and style of the content
2. The type of content
3. Average engagement of audience based on comments, likes, and views
4. Account activity - based on the last post date and amount of posts in the last 30 days.
5. Does style of texts resonates with the description

#RULES:
- Max response length - 500 words
- Don't add a summary, just provide the overview in list format
- Follow the style of the example, start details from the new line
- Use the same language as the description

#EXAMPLE:
1. **Tone and Style of Content**.
The tone of the Instagram account's content is predominantly friendly, emotional, and supportive. The author uses a direct, conversational style that conveys sincerity and openness. Frequent invitations to interact in the comments and emotional expressions help create a sense of engagement and community.

2. **Type of Content**.
The content includes a variety of formats: videos (Reels), images, and text posts. A key theme is video production, tips for creating content, the author's personal stories, and educational materials related to mobile filming and storytelling. Another aspect of the content involves personal reflections on life, emotional experiences, and stories related to war and emigration.

3. **Average Audience Engagement**.
According to the data, posts typically receive between 200 and 2,156 likes and between 9 and 89 comments. Video posts featuring filming insights and tips are particularly popular. The average number of views ranges from several thousand to over 110,000, indicating a high level of engagement.

4. **Account Activity**.
The account is active, with the latest post published on 2021-12-25. In the last 30 days, the account has published 15 posts.

5. **Does the Style of Texts Align with the Description**.
The style of texts and visuals fully aligns with the description. The author offers followers not only professional advice but also personal experiences, effectively creating content that fosters a personal connection. The visuals are fresh, vibrant, and thematically relevant to video production and storytelling, while the texts are accompanied by vivid descriptions and personal notes. As a result, the content appears cohesive and appealing to the audience.

  `,
  });

  messages.push({
    user: PromptMessageUser.User,
    content: `#Posts:
  ${account.data.latestPosts
    .map(
      (post, index) => `
  Post ${index + 1}:
  Caption: ${post.caption}
  Likes: ${post.likesCount}
  Video Views: ${post.videoViewCount}
  Comments: ${post.commentsCount}
  Type: ${post.type}
    `
    )
    .join("\n\n")}
  `,
  });
  return messages;
};

export const getEngagementRateMessages = function (
  account: InstagramDataAccount
) {
  var messages = [] as PromptMessage[];

  messages.push({
    user: PromptMessageUser.System,
    content: `#CONTEXT:
   You are a professional marketer. 
   
   #GOAL:
   You need to review the engagement rate of the Instagram Account for an account type.
   
   #RULES:
   - If the engagement rate is below 3%, then say that the engagement rate is low and suggest improving it
   - Follow the style of the example
   
   #EXAMPLE:
   The engagement rate of **3.24%** for the marketing account is a positive indication of audience interaction. 
   In general, an engagement rate above 3% is considered good on Instagram, 
   suggesting that that content resonates well with your followers.
   `,
  });

  messages.push({
    user: PromptMessageUser.User,
    content: `Account Type:
   ${account.accountType}
   
   Engagement Rate:
   ${account.engagementRate}%
   `,
  });
  return messages;
};

export const getWeaknessMessages = function (account: InstagramProfile) {
  var messages = [] as PromptMessage[];

  messages.push({
    user: PromptMessageUser.System,
    content: `#CONTEXT:
You are a professional marketer. 

#GOAL:
You need to review the Instagram Account and identify its 3 weaknesses.

#OVERVIEW:
${account.generatedData.overview}
  
#ENGAGEMENT:
${account.generatedData.engagement}

#CONTENT OVERVIEW:
${account.generatedData.content}

   
#RULES:
- Follow the style of the example

#EXAMPLE:
  1. **Низька частота публікацій**.
  В обліковому запису спостерігається низька активність, оскільки за останні 30 днів було опубліковано лише 2 пости. Це може вплинути на залучення аудиторії, оскільки регулярне оновлення контенту є ключовим елементом підтримання інтересу підписників.
  
  2. **Потенційна відсутність різноманітності контенту**.
  Хоча контент охоплює різні аспекти відеозйомки та блогінгу, варто розглянути можливість введення нових форматів або тем, які б могли зацікавити ширшу аудиторію. Наприклад, виглядає так, що акцент залишається на навчальних матеріалах, що може обмежити залучення тих, хто шукає також розважальну складову.
  
  3. **Нестача публікацій про особисте життя**.
  Хоча Іра ділиться особистими історіями, це може бути недостатнім для створення глибшого зв'язку з підписниками. Розширення контенту про особисті переживання або думки на актуальні теми могло б допомогти зміцнити відносини з аудиторією та заохотити до обговорення..

`,
  });

  return messages;
};

export const getStrengthMessages = function (account: InstagramProfile) {
  var messages = [] as PromptMessage[];

  messages.push({
    user: PromptMessageUser.System,
    content: `#CONTEXT:
You are a professional marketer. 

#GOAL:
You need to review the Instagram Account and identify its 3 strengths.

#OVERVIEW:
${account.generatedData.overview}
  
#ENGAGEMENT:
${account.generatedData.engagement}

#CONTENT OVERVIEW:
${account.generatedData.content}

#RULES:
- Follow the style of the example

#EXAMPLE:
1. **Експертиза та унікальність**.
Олена Астахова, позиціонуючи себе як "Мама Сторителінгу" та творець першої школи сторителінгу в Україні, демонструє глибоке розуміння теми. Це надає її контенту авторитетності та приваблює тих, хто бажає розвивати свої навички оповіді. Унікальний підхід до навчання та творчості, вказує на значний внесок в індустрію.

2. **Високий рівень залучення аудиторії**.
Залучення на рівні 14.08% є надзвичайно вражаючим і перевищує середні показники для Instagram. Це вказує на те, що контент резонує з підписниками, викликає інтерес і стимулює активну взаємодію. Високий рівень лайків і коментарів підкріплює цю тенденцію.

3. **Емоційний та мотиваційний контент**.
Тон і стиль контенту є емоційними та надихаючими, що допомагає створити глибший зв'язок з аудиторією. Олена ділиться особистими переживаннями, що не лише залучає увагу, а й заохочує підписників до самовираження та розвитку. Формат відео, зокрема, дає можливість показати її особистість і стиль, що сприяє взаємодії.  

`,
  });

  return messages;
};

export const getMonetizationMessages = function (account: InstagramProfile) {
  var messages = [] as PromptMessage[];

  messages.push({
    user: PromptMessageUser.System,
    content: `#CONTEXT:
You are a professional marketer that is specialized in Instagram marketing.

#GOAL:
You need to review the Instagram Account and analyze monetization strategies.

#OVERVIEW:
${account.generatedData.overview}
  
#ENGAGEMENT:
${account.generatedData.engagement}

#CONTENT OVERVIEW:
${account.generatedData.content}

#POSTS:
  ${account.data.latestPosts
    .map(
      (post, index) => `
  Post ${index + 1}:
  Caption: ${post.caption}
  Type: ${post.type}
    `
    )
    .join("\n\n")}

#RESPONSE GUIDELINES:
- Max response length - 200 words
- Is there any monetization strategy applied in the account? 
- Recommend 3 strategies to improve the account's performance

#RESPONSE EXAMPLE:
Акаунт Аліни Морозової демонструє певні стратегії монетизації через продаж консультацій та вебінарів. Проте, щоб підвищити ефективність акаунту та залучити більше аудиторії, варто розглянути такі стратегії:

  1. **Курси та майстер-класи**.
  Створити структуровані онлайн-курси чи серії вебінарів на теми, що викликають найбільший інтерес у підписників. Це може залучити більше платних учасників і розширити аудиторію.

  2. **Партнерство з брендами**.
  Розглянути можливість співпраці з брендами, що пропонують товари або послуги в сфері саморозвитку, психічного здоров'я або досконалості. Це може включати спонсорські пости.

  3. **Контент на Patreon чи у закритих групах**.
  Ввести модель підписки на платний контент, наприклад, через Patreon, де підписники отримують доступ до ексклюзивних практик, сесій запитань і відповідей або індивідуальних консультацій.

Ці стратегії допоможуть не лише монетизувати контент, але і загалом підвищити зацікавленість підписників та збільшити взаємодію з акаунтом.

`,
  });

  return messages;
};
